import { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import Close from "../../components/Atomos/Icons/Close";

import { useEventLoader } from "../../hooks/Events/useEventLoader";

import { getEventNetworkingMap } from "../../Services/EventNetworkingMap";

import NetworkingMapGraph from "./networkingMap/NetworkingMapGraph";

import NetworkingMapHeaderPanel from "./networkingMap/NetworkingMapHeaderPanel";

import NetworkingMapLevelLegend from "./networkingMap/NetworkingMapLevelLegend";

import NetworkingMapPersonPanel from "./networkingMap/NetworkingMapPersonPanel";

import {
  withFallbackAvatars,
  withVerifiedAvatars,
} from "./networkingMap/networkingMapAvatar";

import { getRootId } from "./networkingMap/networkingMapCenter";

import {
  ALL_NETWORKING_CATEGORY_KEYS,
  type NetworkingCategoryKey,
} from "./networkingMap/networkingMapCategories";

import type { NetworkingMapResponse } from "./networkingMap/networkingMapTypes";

const EMPTY_MAP: NetworkingMapResponse = { nodes: [], edges: [] };

export default function NetworkingMapPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const event = useEventLoader(id);

  const pageHeading = event?.title ? `Connections: ${event.title}` : "Connections";

  const [eventMap, setEventMap] = useState<NetworkingMapResponse | null>(null);
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<
    NetworkingCategoryKey[]
  >(ALL_NETWORKING_CATEGORY_KEYS);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }

    let isActive = true;

    setIsLoadingMap(true);
    setSelectedNodeId(null);

    getEventNetworkingMap(id, { categories: selectedCategories })
      .then((response) => {
        if (isActive) {
          setEventMap(response ?? EMPTY_MAP);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingMap(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [id, selectedCategories]);

  /** At least one category must stay active; otherwise the API falls back to all six. */
  const toggleCategory = useCallback(
    (category: NetworkingCategoryKey, checked: boolean) => {
      setSelectedCategories((current) => {
        if (checked) {
          return current.includes(category) ? current : [...current, category];
        }

        if (current.length === 1) {
          return current;
        }

        return current.filter((selected) => selected !== category);
      });
    },
    [],
  );

  const displayMap = eventMap ?? EMPTY_MAP;
  const isMapLoading = isLoadingMap;

  const preparedMap = useMemo(() => {
    if (!eventMap) {
      return EMPTY_MAP;
    }
    return withFallbackAvatars(eventMap);
  }, [eventMap]);

  const [verifiedMap, setVerifiedMap] =
    useState<NetworkingMapResponse | null>(null);

  useEffect(() => {
    setVerifiedMap(null);
    let isActive = true;
    withVerifiedAvatars(preparedMap).then((verified) => {
      if (isActive) {
        setVerifiedMap(verified);
      }
    });
    return () => {
      isActive = false;
    };
  }, [preparedMap]);

  const graphData = verifiedMap ?? preparedMap;

  const matchCount = displayMap.edges.length;

  const rootNode = useMemo(() => {
    const rootId = getRootId(graphData);

    return graphData.nodes.find((node) => node.data.id === rootId);
  }, [graphData]);

  const selectedNode = graphData.nodes.find(
    (node) => node.data.id === selectedNodeId,
  );

  return (
    <div className="bg-white p-6 text-left md:p-8">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 self-stretch">
          <h1 className="min-w-0 flex-1 font-['Roboto'] text-[24px] font-semibold leading-normal text-gray-font">
            {pageHeading}
          </h1>
          <button
            type="button"
            aria-label="Close connections"
            className="shrink-0 text-gray-font transition-colors hover:text-primary"
            onClick={() => navigate(`/events/${id}/attendees`)}
          >
            <Close className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col gap-8">
          <NetworkingMapHeaderPanel
            name={rootNode?.data.name ?? "My profile"}
            avatar={rootNode?.data.avatar}
            jurisdictionLabel={rootNode?.data.jurisdictionLabel}
            location={rootNode?.data.location}
            matchCount={matchCount}
            selectedCategories={selectedCategories}
            onToggleCategory={toggleCategory}
            filtersDisabled={isMapLoading}
            isLoading={isMapLoading}
          />

          <NetworkingMapLevelLegend />
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
          <div className="min-h-0 min-w-0 flex-1">
            {isMapLoading ? (
              <div
                className="flex h-[min(420px,55vh)] items-center justify-center rounded-lg bg-[#F6F6F6] sm:h-[480px] lg:h-[560px]"
                aria-busy="true"
                aria-label="Loading connections map"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="h-12 w-12 animate-pulse rounded-full bg-[#D6D6D6]" />
                  <p className="font-['Roboto'] text-sm text-[#474F56]">
                    Loading connections...
                  </p>
                </div>
              </div>
            ) : (
              <NetworkingMapGraph
                data={graphData}
                selectedNodeId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
                className="h-[min(420px,55vh)] sm:h-[480px] lg:h-[560px]"
              />
            )}
          </div>

          {selectedNode && <NetworkingMapPersonPanel node={selectedNode} />}
        </div>
      </div>
    </div>
  );
}
